import React, { useState } from 'react';
// import { CrudAddModalProps, CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, FileButton, Group, Loader, Select, Stack, TextInput, Tooltip } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconFilePlus } from '@tabler/icons-react';
import StringUtils from '@dr-pam/common-components/Utils/StringUtils';
import { v4 as uuid } from 'uuid';
import useLoadingEffect from '@dr-pam/common-components/Hooks/useLoadingEffect';
import type { CategoryListFragment, SectionImageListFragment, SectionImageSingleFragment } from '../../graphql/graphql';
import type { CategorySortableTreeItem } from '../../services/CategoryService';
import { useCategoryService } from '../../services/CategoryService';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import { useS3ObjectStorageService } from '@dr-pam/common-components/Services/S3ObjectStorageService';
import z from 'zod';
import type { CrudAddModalProps } from '../crud/CrudTable';
import { useSectionImageService } from '../../services/SectionImageService';

export const validationSchema = z.object({
	filename: z.string().min(1).max(512),
});

export type AddCategoryImageModal = CrudAddModalProps<SectionImageListFragment, SectionImageSingleFragment> & {
	categoryId?: string;
};

export default function AddCategoryImageModal(props: AddCategoryImageModal) {
	const { modalId, onCancel, onCreated, categoryId } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();
	const [categories, setCategories] = useState<CategorySortableTreeItem<CategoryListFragment>[]>([]);
	const [fileToUpload, setFileToUpload] = useState<File | null>(null);

	const s3ObjectStorageService = useS3ObjectStorageService();
	const categoryService = useCategoryService();
	const sectionImageService = useSectionImageService();

	const isLoadingCategories = useLoadingEffect(async () => {
		try {
			const response = await categoryService.getAllInFlattenedTree().response;
			setCategories(response);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to load categories');
		}
	}, [categoryService, categoryId]);

	const form = useForm<AddImageForm>({
		initialValues: {
			url: '',
			filename: '',
			categoryId: categoryId ?? '',
		},
		validate: schemaResolver(validationSchema),
	});

	const handleFileSelected = (file: File | null) => {
		if (file) {
			form.setValues({
				filename: file.name,
				url: StringUtils.slugifyForFilesystem(file.name),
			});
		} else {
			form.setValues({
				url: '',
				filename: '',
			});
		}
		setFileToUpload(file);
	};

	const handleSubmit = async (values: AddImageForm) => {
		if (!fileToUpload) {
			return;
		}

		const loader = addLoader();

		try {
			const imageId = uuid();
			const taggedFileName = `category-images/${categoryId}/${imageId}/${Date.now()}/${fileToUpload.name}`;
			const uploadRequest = s3ObjectStorageService.uploadFile(taggedFileName, fileToUpload);
			await uploadRequest.response;
			const url = s3ObjectStorageService.getRedirectUrl(taggedFileName);

			const createdSectionImage = await sectionImageService.create({
				id: imageId,
				filename: fileToUpload.name,
				url,
				...((categoryId || values.categoryId) && {
					sectionImageCategories: {
						create: [
							{
								category: {
									connect: {
										id: categoryId || values.categoryId,
									},
								},
							},
						],
					},
				}),
			});

			NotificationUtils.showSuccess('Successfully added image to category', createdSectionImage.filename ?? '');
			onCreated(createdSectionImage);
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to add image to category');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	const categoryData = (categories ?? []).map((category) => ({
		value: category.item.id,
		label: category.getName(),
	}));

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<Group align="flex-end" justify="flex-end">
						<TextInput
							label="Upload file"
							{...form.getInputProps('filename')}
							withAsterisk
							disabled
							style={{ flexGrow: 1 }}
						/>
						<FileButton onChange={handleFileSelected}>
							{(props) => (
								<Tooltip label="Click to upload file">
									<Button {...props}>
										<IconFilePlus />
									</Button>
								</Tooltip>
							)}
						</FileButton>
					</Group>
					<Select
						{...form.getInputProps('categoryId')}
						data={categoryData}
						label="Initial category"
						placeholder={isLoadingCategories ? 'Loading...' : '--- Please select ---'}
						nothingFoundMessage="No search results"
						disabled={isLoading || isLoadingCategories || categoryId !== undefined}
						searchable
					/>
					<Group justify="flex-end" mt="md">
						<Button
							type="button"
							variant="subtle"
							onClick={handleCancel}
							disabled={isLoading || isLoadingCategories}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading || isLoadingCategories}>
							Add image {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}

export type AddImageForm = {
	url: string;
	filename: string;
	categoryId: string;
};
